import { useEffect, useRef, useState } from "react";
import { REGIONS, setRegion } from "../utils/region";

const TITLE = "Praesidio Studio";
const SUB = "Crafted for India.";
const DEST = "/in";

function prefersReducedMotion() {
  try {
    return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  } catch {
    return false;
  }
}

function typeText(full, setText, msPerChar, signal) {
  return new Promise((resolve) => {
    let i = 0;
    setText("");
    const tick = () => {
      if (signal.aborted) {
        resolve();
        return;
      }
      i += 1;
      setText(full.slice(0, i));
      if (i >= full.length) {
        resolve();
        return;
      }
      timer = window.setTimeout(tick, msPerChar);
    };
    let timer = window.setTimeout(tick, msPerChar);
    signal.addEventListener(
      "abort",
      () => {
        window.clearTimeout(timer);
        resolve();
      },
      { once: true }
    );
  });
}

function wait(ms, signal) {
  return new Promise((resolve) => {
    if (signal.aborted) {
      resolve();
      return;
    }
    const timer = window.setTimeout(resolve, ms);
    signal.addEventListener(
      "abort",
      () => {
        window.clearTimeout(timer);
        resolve();
      },
      { once: true }
    );
  });
}

export default function IndiaSplash({ onComplete }) {
  const [title, setTitle] = useState("");
  const [sub, setSub] = useState("");
  const [showCursor, setShowCursor] = useState(true);
  const [phase, setPhase] = useState("in"); // in | out
  const [readySub, setReadySub] = useState(false);
  const [shouldRender, setShouldRender] = useState(true);
  const started = useRef(false);

  // BFCache safety — page restored from cache after Back press
  useEffect(() => {
    const handlePageShow = (e) => {
      if (e.persisted && !onComplete) {
        setShouldRender(false);
        window.location.replace("/");
      }
    };
    window.addEventListener("pageshow", handlePageShow);
    return () => window.removeEventListener("pageshow", handlePageShow);
  }, [onComplete]);

  useEffect(() => {
    if (started.current) return undefined;
    started.current = true;

    const loader = document.getElementById("loader");
    if (loader) loader.style.display = "none";

    // Back from the studio should return home, not replay this handoff.
    if (!onComplete) {
      try {
        const navType = performance.getEntriesByType?.("navigation")?.[0]?.type;
        if (navType === "back_forward") {
          setShouldRender(false);
          window.location.replace("/");
          return undefined;
        }
      } catch {
        /* ignore – Safari private mode */
      }
    }

    setRegion(REGIONS.IN);
    document.documentElement.classList.add("india-splash-active");

    const ac = new AbortController();
    const { signal } = ac;

    (async () => {
      if (prefersReducedMotion()) {
        setTitle(TITLE);
        setSub(SUB);
        setReadySub(true);
        setShowCursor(false);
        await wait(1200, signal);
      } else {
        await wait(420, signal);
        await typeText(TITLE, setTitle, 52, signal);
        await wait(380, signal);
        setReadySub(true);
        await typeText(SUB, setSub, 38, signal);
        setShowCursor(false);
        await wait(1100, signal);
      }

      if (signal.aborted) return;
      setPhase("out");
      await wait(780, signal);
      if (signal.aborted) return;
      if (onComplete) onComplete();
      else window.location.replace(DEST);
    })();

    return () => {
      ac.abort();
      document.documentElement.classList.remove("india-splash-active");
    };
  }, [onComplete]);

  if (!shouldRender) {
    return null;
  }

  return (
    <div
      className={`in-splash${phase === "out" ? " is-out" : ""}`}
      role="status"
      aria-live="polite"
      aria-label="Entering Praesidio Studio"
    >
      <div className="in-splash-glow" aria-hidden="true" />
      <div className="in-splash-inner">
        <p className="in-splash-kicker">India</p>
        <h1 className="in-splash-title">
          <span>{title}</span>
          {showCursor && !readySub ? (
            <span className="in-splash-caret" aria-hidden="true" />
          ) : null}
        </h1>
        <p className={`in-splash-sub${readySub ? " is-on" : ""}`}>
          <span>{sub}</span>
          {showCursor && readySub ? (
            <span className="in-splash-caret" aria-hidden="true" />
          ) : null}
        </p>
        <div className="in-splash-bar" aria-hidden="true">
          <span className="in-splash-bar-fill" />
        </div>
      </div>
    </div>
  );
}
